import SV from 'server-vars';
import { defineMessages, IntlShape } from 'react-intl';
import { stringify } from 'qs';
import { noticeError } from './shared/helpers/newrelic';

/**
 * @deprecated
 * refactor everything to use ./shared/helpers/logError - only doing this so tests pass
 * Log an error to NewRelic (if available); otherwise, log to console.error.
 * @param {Error | string} error
 * @param [string] message Optional prefix to console log.
 */

export function logError(error: Error | string, message?: string): void {
    if (typeof error === 'string') {
        error = new Error(error);
    }

    noticeError(error, message || 'Exception caught:');
}

export type DeepLinkHandlers = {
    onCallCompleted: () => void;
    registerDeepLink: (node: HTMLElement, moduleHash: string) => void;
};

type DeepLinkHandlerState = {
    hash: string;
    scrolled: boolean;
    elements: { [key: string]: HTMLElement };
    callCount: number;
    callsCompleted: number;
};

export function setupDeepLinkHandler(callCount: number): DeepLinkHandlers {
    const hash = window.location.hash;
    if (!hash) {
        return {
            onCallCompleted: () => {},
            registerDeepLink: () => {},
        };
    }

    const state: DeepLinkHandlerState = {
        hash,
        scrolled: false,
        elements: {},
        callCount,
        callsCompleted: 0,
    };

    return {
        onCallCompleted: () => {
            state.callsCompleted++;
            const node = state.elements[state.hash];

            if (node && !state.scrolled && state.callsCompleted >= state.callCount) {
                node.scrollIntoView();
                state.scrolled = true;
            }
        },
        registerDeepLink: (node: HTMLElement, moduleHash: string) => {
            state.elements[moduleHash] = node;
        },
    };
}

export const mailMessages = defineMessages({
    subject: {
        id: 'abt.interiorsProfile.photo.share.email.subject',
        defaultMessage: 'Design Inpsiration on 1stdibs',
    },
    body: {
        id: 'abt.interiorsProfile.photo.share.email.body',
        defaultMessage: 'Check out {firmName}\'s "{projectName}" project on 1stdibs. {projectUrl}',
    },
    pinterest: {
        id: 'abt.interiorsProfile.share.pinterest',
        defaultMessage: 'See more of {firmName}\'s "{projectName}" on 1stdibs',
    },
});

// trailing slash can be removed once
// https://1stdibs.atlassian.net/browse/TRADE-2508
// is fixed
export const getSharePath: (projectUrl: string) => string = projectUrl =>
    `${SV.get('localeOrigin')}${projectUrl}/`;

export const prepShareWindow: () => string = () => {
    const width = 665;
    const height = 500;

    const windowOptions = {
        scrollbars: 'yes',
        resizable: 'no',
        toolbar: 'no',
        height: height,
        width: width,
        top: 0,
        left: 0,
    };

    const screenHeight = screen.height;
    const screenWidth = screen.width;

    const halfScreenWidth = screenWidth / 2;
    const halfWidth = width / 2;
    windowOptions.left = Math.round(halfScreenWidth - halfWidth);

    if (screenHeight > height) {
        const halfScreenHeight = screenHeight / 2;
        const halfHeight = height / 2;

        windowOptions.top = Math.round(halfScreenHeight - halfHeight);
    }
    const windowString = (Object.keys(windowOptions) as (keyof typeof windowOptions)[])
        .reduce((prev, curr) => {
            prev.push(`${curr}=${windowOptions[curr]}`);
            return prev;
        }, [] as string[])
        .join(',');

    return windowString;
};

type CommonPinterestShare = {
    url: string;
    photoPath: string;
};

type ShareWithDescription = CommonPinterestShare & {
    description: string;
};

type ShareWithFirmAndProject = CommonPinterestShare & {
    firmName: string;
    projectName: string;
    intl: IntlShape;
};

type PinterestShare = ShareWithDescription | ShareWithFirmAndProject;

function isShareWithDescription(input: PinterestShare): input is ShareWithDescription {
    return (input as ShareWithDescription).description !== undefined;
}

export function shareViaPinterest(input: PinterestShare): void {
    const windowString = prepShareWindow();
    const pinWindow = window.open('', 'pinterest', windowString);

    let description = null;
    if (isShareWithDescription(input)) {
        description = input.description;
    } else {
        const { firmName, projectName } = input;
        description = input.intl.formatMessage(mailMessages.pinterest, {
            firmName,
            projectName,
        });
    }

    if (!description || !pinWindow) {
        return;
    }

    pinWindow.location.href = `https://pinterest.com/pin/create/button/?${stringify({
        url: getSharePath(input.url),
        media: input.photoPath,
        description,
    })}`;
}

export function shareViaTwitter(text: string, url: string): void {
    const windowString = prepShareWindow();
    const tweetWindow = window.open('', 'twitter', windowString);
    const windowOptions = { text, url };

    if (tweetWindow) {
        tweetWindow.location.href = `https://twitter.com/intent/tweet?${stringify(windowOptions)}`;
    }
}

export function shareViaFacebook(photoImgPath: string | null): void {
    if (!window.FB || !window.FB.ui || !photoImgPath) {
        console.error('Could not load FB share');
        return;
    }

    window.FB.ui({
        method: 'stream.share',
        href: window.location.href,
        image: photoImgPath,
    });
}

type EmailShare = {
    subject: string;
    body: string;
    url: string;
};

export function shareViaEmail({ subject, body, url }: EmailShare): void {
    const subjectLine = encodeURIComponent(subject);
    const bodyText = `${encodeURIComponent(body)}%0D%0A%0D%0A${url}`;

    window.location.href = `mailto:?subject=${subjectLine}&body=${bodyText}`;
}

/**
 * GraphQL objects that we expect somewhere but came back null. Unexpected
 * because typically they're only marked as nullable because of generated
 * flow types
 */
type UnexpectedNullGraphQLObjectName = 'project' | 'tradeFirm';

/**
 * Checks if a value is null that is actually not expected to be null but was
 * generated as such from the GraphQL schema. Can check fields as well as
 * objects.
 */
export function logUnexpectedNullGraphQLError(
    objectName: UnexpectedNullGraphQLObjectName,
    fieldName: string | null
): void {
    if (fieldName) {
        logError(`Expected field "${fieldName}" on GraphQL object "${objectName}" to not be null`);
        return;
    }
    logError(`Expected GraphQL object "${objectName}", got null instead`);
}
